import type { DeclarationDApp, SettingsDoor } from '@taki/ui';
import { cleDuJeu, definitionsDeJeux, enTetesDeLaPorte, masquesDuJeu, type EnTeteDeFiche } from '@taki/data-model';
import { lireOptionsChamp } from '../studio/client/options.js';
import { champsDEnTete, type ChampDEnTete } from '../studio/enTete.js';
import { ecransSuivants, PREFIXE_ECRAN } from '../studio/ecransEdition.js';
import type { EcransLus } from '../studio/modele.js';
import { MOTS_EN_TETE } from '../studio/identifiants.mjs';

/**
 * CE QUE LE HUB PARAMETRES LIT D'UNE DECLARATION, pour les reglages d'une
 * porte : la liste des portes du rail, et les listes de choix de « Étiquettes
 * et listes ». Module PUR (ni React, ni `import.meta.env`) : le hub et le
 * panneau le lisent, un test Node l'importe tel quel.
 */

/** Prefixe des ids de menu des apps : evite toute collision avec "dock". */
export const APP_MENU_PREFIX = 'app:';

export type PorteDeReglages = SettingsDoor & { menuId: string; label: string };

/**
 * LES PORTES D'UNE APP DANS LE RAIL DES PARAMETRES, TOUTES. Une porte hors
 * navigation (`navigation: false`, la porte Tâches de Timesheet) y a sa ligne
 * comme les autres : on n'y entre que par un lien, mais ses listes et son
 * affichage se reglent ici, et nulle part ailleurs (retour du fondateur du
 * 14/10/2026). Sans elle, `?t=app:<id>/porte:<ecran>/<porte>` retombait sur Dock.
 */
export function portesDeReglages(appId: string, declaration: DeclarationDApp): PorteDeReglages[] {
  return declaration.ecrans.ecrans.flatMap(ecran => ecran.portes.map(porte => ({
    screenId: ecran.id, doorId: porte.id, label: porte.label,
    menuId: `${APP_MENU_PREFIX}${appId}/porte:${encodeURIComponent(ecran.id)}/${encodeURIComponent(porte.id)}`,
  })));
}

export type CatalogueDePorte = ReturnType<typeof lireOptionsChamp> & { jeu: string; id: string };

/**
 * LES LISTES DE CHOIX D'UNE PORTE (ou de toute l'app, sans porte) : TOUTES
 * CELLES DE SON JEU, et rien que ca. Meme resolution et memes cibles que le
 * Studio, choix natifs compris.
 *
 * LES LISTES APPARTIENNENT AU JEU, PAS A LA PORTE NI A L'APP (regle du
 * fondateur du 14/10/2026, `docs/socle/jeu-listes.md`). Toute porte qui montre
 * un jeu, dans toute app, cachee ou non, regle donc les MEMES listes : chaque
 * champ a choix du jeu (le Service qui titre une tache chez TAC compris), puis
 * chaque liste du socle que les portes du jeu citent (Étape, Priorité), hors
 * des champs que le jeu masque (`jeux[].masques`). Rien
 * n'est choisi a la main, rien n'est filtre selon ce que la porte cite : la
 * declaration, recalee sur les jeux de l'org (`declarationCourante`), suffit.
 */
export function cataloguesDePorte(declaration: DeclarationDApp, selection: SettingsDoor | null): CatalogueDePorte[] {
  const ecranChoisi = selection ? declaration.ecrans.ecrans.find(ecran => ecran.id === selection.screenId) : undefined;
  const porteChoisie = ecranChoisi?.portes.find(porte => porte.id === selection?.doorId);
  if (selection && !porteChoisie?.jeu) return [];
  const definitions = definitionsDeJeux(declaration);
  const portes = declaration.ecrans.ecrans.flatMap(ecran => ecran.portes);
  const catalogues: CatalogueDePorte[] = [];
  for (const jeu of declaration.champs.jeux) {
    const cle = cleDuJeu(jeu);
    if (porteChoisie?.jeu && cleDuJeu(porteChoisie.jeu) !== cle) continue;
    /* LE NOM DU JEU, le meme d'une porte et d'une app a l'autre : le sien, ou
       celui que sa premiere porte lui donne. */
    const premiere = portes.find(porte => porte.jeu !== undefined && cleDuJeu(porte.jeu) === cle);
    const nomFiche = jeu.nom || premiere?.jeu?.nom || premiere?.label || jeu.type || jeu.family;
    /* UN CHAMP MASQUE (`jeux[].masques`) ne se montre sur aucune fiche : sa liste ne se regle pas ici non plus. */
    const masques = masquesDuJeu(jeu);
    const aChoix = jeu.champs.filter(champ => (champ.type === 'select' || champ.type === 'multiselect') && !masques.has(champ.key)).map(champ => champ.key);
    const natives = Object.keys(definitions.get(cle)?.natives ?? {});
    for (const field of [...aChoix, ...natives]) {
      const cible = { family: jeu.family, ...(jeu.type === undefined ? {} : { type: jeu.type }), field };
      catalogues.push({ ...lireOptionsChamp(declaration, cible), jeu: nomFiche, id: `${jeu.family}:${jeu.type ?? ''}:${field}` });
    }
  }
  return catalogues;
}

/**
 * « EN TÊTE DE FICHE » D'UNE PORTE, dans ses Parametres (chantier
 * `fiche-suivi`) : les en-tetes montres dans la rangee 3 de ses fiches, dans
 * l'ordre (`enTetesDeLaPorte`, l'ancien objet seul lu comme une liste d'un),
 * chacun son champ et son mode, et les champs offerts (`champsDEnTete`, la
 * meme lecture que le Studio). `null` hors d'une porte, ou pour une porte sans jeu.
 */
export interface EnTeteDePorte {
  rang: number;
  porteId: string;
  champs: ChampDEnTete[];
  enTetes: EnTeteDeFiche[];
}

export function enTeteDePorte(declaration: DeclarationDApp, selection: SettingsDoor | null): EnTeteDePorte | null {
  if (selection === null) return null;
  const rang = declaration.ecrans.ecrans.findIndex((ecran) => ecran.id === selection.screenId);
  const ecran = declaration.ecrans.ecrans[rang];
  const porte = ecran?.portes.find((p) => p.id === selection.doorId);
  if (ecran === undefined || porte?.jeu === undefined) return null;
  return {
    rang,
    porteId: porte.id,
    champs: champsDEnTete(ecran as unknown as Parameters<typeof champsDEnTete>[0], porte.id, declaration.champs.jeux as unknown as Parameters<typeof champsDEnTete>[2]),
    enTetes: enTetesDeLaPorte(porte),
  };
}

/**
 * LA DECLARATION APRES LE REGLAGE, par le MEME moteur que le Studio et ses
 * outils MCP (`ecransSuivants`, lignes `ecran:<r>:porte:<p>:etapes:<rang>` et
 * `:etapes:<rang>:mode`) : un seul chemin d'ecriture pour un seul reglage.
 * `enTetes` est la liste COMPLETE voulue : chaque rang s'ecrit, les rangs lus
 * au-dela se vident. Une liste vide retire la clef (rien dans la rangee 3).
 */
export function declarationAvecEnTete(
  declaration: DeclarationDApp,
  enTete: EnTeteDePorte,
  enTetes: readonly EnTeteDeFiche[],
): DeclarationDApp {
  const base = `${PREFIXE_ECRAN}${enTete.rang}:porte:${enTete.porteId}:etapes`;
  const changements: [string, string][] = [
    ...enTetes.flatMap((e, place): [string, string][] => [[`${base}:${place}`, e.champ], [`${base}:${place}:mode`, e.mode]]),
    ...enTete.enTetes.slice(enTetes.length).map((_, i): [string, string] => [`${base}:${enTetes.length + i}`, '']),
  ];
  const ecrans = ecransSuivants({ ecrans: declaration.ecrans as unknown as EcransLus, changements });
  return { ...declaration, ecrans: ecrans as unknown as DeclarationDApp['ecrans'] };
}

/**
 * LES RAISONS DU CADENAS de « En tête de fiche », dites UNE fois, au survol du
 * cadenas du titre (`SettingsSection lock`) : jamais en note ni sur une ligne.
 * Pendant le chargement, pas de cadenas : les controles attendent, eteints.
 */
export const RAISONS_EN_TETE = Object.freeze({
  indisponible: 'Modification indisponible.',
  chargement: 'Chargement…',
  modele: 'Sur le modèle, se règle au Studio.',
  membre: 'Réservé aux administrateurs.',
});

/** Pourquoi on ne peut pas ecrire, ou `null`. Sur `master`, on ne l'ecrit jamais ici. */
export function raisonDEnTete(port: { pret: boolean; peutModifier: boolean } | null, orgId: string): string | null {
  if (port === null) return RAISONS_EN_TETE.indisponible;
  if (!port.pret) return RAISONS_EN_TETE.chargement;
  if (orgId === 'master') return RAISONS_EN_TETE.modele;
  if (!port.peutModifier) return RAISONS_EN_TETE.membre;
  return null;
}

/** Un encadre de la section : sa legende, son champ, son mode, et les champs qu'il peut prendre. */
export interface GroupeDEnTete {
  legende: string;
  champ: string;
  mode: 'flux' | 'selection';
  options: { value: string; label: string }[];
}

/** Ce que la section montre, decide ici pour que le panneau ne fasse que rendre. */
export interface SectionDEnTete {
  /** La raison du SEUL cadenas, contre le titre ; `undefined` quand on peut ecrire ou qu'on attend. */
  lock: string | undefined;
  /** Tous les controles eteints (verrou, chargement, ecriture en cours). */
  inerte: boolean;
  /** L'etat a dire a la place des encadres, ou `null`. */
  vide: string | null;
  /** Il y a quelque chose a regler : l'ajout et l'enregistrement se montrent. */
  reglable: boolean;
  groupes: GroupeDEnTete[];
  /** « Ajouter un en-tête » : possible, ou la raison qui l'eteint (un ETAT). */
  ajout: { possible: boolean; raison: string | null };
  /** La liste reglee differe de celle lue : Annuler et Enregistrer s'allument. */
  change: boolean;
}

/**
 * LA SECTION « EN TÊTE DE FICHE » D'UNE PORTE, en donnees. `brouillon` est la
 * liste en cours de reglage (`null` : celle de la declaration). Un champ deja
 * en tete n'est offert qu'a son propre encadre ; un champ qui a quitte le jeu
 * reste lisible, nomme comme tel.
 */
export function sectionDEnTete(
  enTete: EnTeteDePorte | null,
  brouillon: readonly EnTeteDeFiche[] | null,
  raison: string | null,
  occupe: boolean,
): SectionDEnTete {
  const lock = raison !== null && raison !== RAISONS_EN_TETE.chargement ? raison : undefined;
  const inerte = raison !== null || occupe;
  const lus = enTete?.enTetes ?? [];
  const liste = brouillon ?? lus;
  const change = JSON.stringify(liste) !== JSON.stringify(lus);
  if (enTete === null) return { lock, inerte, vide: 'Cette porte ne montre aucun jeu.', reglable: false, groupes: [], ajout: { possible: false, raison: null }, change: false };
  if (enTete.champs.length === 0 && liste.length === 0) {
    return { lock, inerte, vide: `${MOTS_EN_TETE.sansListe}.`, reglable: false, groupes: [], ajout: { possible: false, raison: null }, change: false };
  }
  const pris = new Set(liste.map((e) => e.champ));
  const libres = enTete.champs.filter((c) => !pris.has(c.value));
  const groupes = liste.map((e, rang) => ({
    legende: MOTS_EN_TETE.rang(rang),
    champ: e.champ,
    mode: e.mode,
    options: [
      ...enTete.champs.filter((c) => c.value === e.champ || !pris.has(c.value)),
      ...(enTete.champs.some((c) => c.value === e.champ) ? [] : [{ value: e.champ, label: MOTS_EN_TETE.retire }]),
    ],
  }));
  return {
    lock,
    inerte,
    vide: liste.length === 0 ? 'Aucun en-tête.' : null,
    reglable: true,
    groupes,
    ajout: { possible: !inerte && libres.length > 0, raison: libres.length === 0 ? MOTS_EN_TETE.complet : null },
    change,
  };
}

/** Un en-tete de plus, sur le premier champ libre, en Étapes ; la liste telle quelle sans champ libre. */
export function ajouterEnTete(liste: readonly EnTeteDeFiche[], champs: readonly ChampDEnTete[]): EnTeteDeFiche[] {
  const pris = new Set(liste.map((e) => e.champ));
  const libre = champs.find((c) => !pris.has(c.value));
  return libre === undefined ? [...liste] : [...liste, { champ: libre.value, mode: 'flux' }];
}

/** L'en-tete de ce rang s'en va ; les suivants remontent. */
export function retirerEnTete(liste: readonly EnTeteDeFiche[], rang: number): EnTeteDeFiche[] {
  return liste.filter((_, i) => i !== rang);
}

/** Le champ ou le mode d'un en-tete change ; un champ deja tenu par un AUTRE en-tete est refuse. */
export function changerEnTete(liste: readonly EnTeteDeFiche[], rang: number, patch: Partial<EnTeteDeFiche>): EnTeteDeFiche[] {
  if (patch.champ !== undefined && liste.some((e, i) => i !== rang && e.champ === patch.champ)) return [...liste];
  return liste.map((e, i) => (i === rang ? { ...e, ...patch } : e));
}

/**
 * « MODIFIER DANS LE TABLEAU » D'UNE PORTE, dans ses Parametres : le reglage
 * `modifierDansLeTableau` de la declaration de l'org. `null` hors d'une porte,
 * ou pour une porte qui ne monte pas le tableau (`core.tableau`, la vue de la
 * porte, sinon celle de l'ecran) : un reglage vit la ou il s'applique, et sans
 * tableau la clef ne gouvernerait rien.
 */
export interface ModifierDansLeTableauDePorte {
  rang: number;
  porteId: string;
  actif: boolean;
}

export function modifierDansLeTableauDePorte(declaration: DeclarationDApp, selection: SettingsDoor | null): ModifierDansLeTableauDePorte | null {
  if (selection === null) return null;
  const rang = declaration.ecrans.ecrans.findIndex((ecran) => ecran.id === selection.screenId);
  const ecran = declaration.ecrans.ecrans[rang];
  const porte = ecran?.portes.find((p) => p.id === selection.doorId);
  if (ecran === undefined || porte === undefined) return null;
  const vues: readonly string[] = porte.vues ?? ecran.vues;
  if (!vues.includes('core.tableau')) return null;
  return { rang, porteId: porte.id, actif: porte.modifierDansLeTableau === true };
}

/**
 * LA DECLARATION APRES LE BASCULEMENT, par le MEME moteur que le Studio et ses
 * outils MCP (`ecransSuivants`, ligne `ecran:<r>:porte:<p>:modifierDansLeTableau`) :
 * allumer ecrit `true`, eteindre RETIRE la clef (l'absence vaut non).
 */
export function declarationAvecModifierDansLeTableau(
  declaration: DeclarationDApp,
  reglage: ModifierDansLeTableauDePorte,
  actif: boolean,
): DeclarationDApp {
  const changements: [string, string][] = [[`${PREFIXE_ECRAN}${reglage.rang}:porte:${reglage.porteId}:modifierDansLeTableau`, actif ? 'oui' : '']];
  const ecrans = ecransSuivants({ ecrans: declaration.ecrans as unknown as EcransLus, changements });
  return { ...declaration, ecrans: ecrans as unknown as DeclarationDApp['ecrans'] };
}

/** Ce que la section « Édition » montre, decide ici pour que le panneau ne fasse que rendre. */
export interface SectionDEdition {
  /** La raison du SEUL cadenas, contre le titre ; `undefined` quand on peut ecrire ou qu'on attend. */
  lock: string | undefined;
  /** L'interrupteur est eteint (verrou, chargement, ecriture en cours). */
  inerte: boolean;
  /** La section existe : la porte monte le tableau. */
  visible: boolean;
  /** L'etat courant du reglage. */
  actif: boolean;
}

export const TITRE_EDITION = 'Édition';
export const LIBELLE_MODIFIER_DANS_LE_TABLEAU = 'Modifier dans le tableau';

export function sectionDEdition(
  reglage: ModifierDansLeTableauDePorte | null,
  raison: string | null,
  occupe: boolean,
): SectionDEdition {
  return {
    lock: raison !== null && raison !== RAISONS_EN_TETE.chargement ? raison : undefined,
    inerte: raison !== null || occupe,
    visible: reglage !== null,
    actif: reglage?.actif === true,
  };
}
